import produce, { Draft } from 'immer';
import { intersection, isUndefined } from 'lodash';
import { getType } from 'typesafe-actions';

import { getValidationErrorData } from '../common/helpers';
import { ResourceType } from '../common/typings';

import {
    apiActionError,
    applyItem,
    categoriesDataReceived,
    changeEnabledGalleryTypes,
    changeRecentNameFilter,
    changeRecentOrder,
    changeSelectedGalleryCategories,
    changeSemanticTextFilter,
    closeGalleryPopup,
    editItemName,
    fetchInitialData,
    fetchItemsSuccess,
    GalleryAction,
    hoverItem,
    loadMoreItems,
    loadMoreItemsSuccess,
    openGalleryPopup,
    previewAudioItem,
    previewError,
    previewImageItem,
    previewVideoItem,
    removeGalleryItemSuccess,
    removeItem,
    selectTab,
    videoPreviewDataReceived
} from './actions';
import { galleryTypesNeedToFetchOnOpen, getGalleriesInitialState } from './helpers';
import { selectCurrentGallery, selectGalleryPane, selectRecentGalleryPane, selectStockGalleryPane } from './selectors';
import { GalleriesState, GalleryPaneTypes } from './typings';

export function galleries(state: GalleriesState = getGalleriesInitialState(), action: GalleryAction): GalleriesState {
    return produce(state, (draft: Draft<GalleriesState>) => {
        switch (action.type) {
            case getType(categoriesDataReceived): {
                const { currentResourceType } = draft;
                const { rawData } = action.fetchResult;

                if (currentResourceType === ResourceType.image) {
                    draft.imageStockCategoriesData = rawData;

                    return;
                }

                if (currentResourceType === ResourceType.video) {
                    draft.videoStockCategoriesData = rawData;
                    return;
                }

                return;
            }

            case getType(closeGalleryPopup): {
                draft.error = undefined;
                draft.isPopupOpened = false;

                return;
            }

            case getType(openGalleryPopup): {
                const { resourceType, paneType, fileEnabledTypes, fileEditorId } = action;
                const gallery = selectCurrentGallery(draft, resourceType);

                draft.currentResourceType = resourceType;
                draft.isPopupOpened = true;

                gallery.isBusy = false;
                gallery.currentPaneType = paneType;
                gallery.fileEditorId = fileEditorId;

                if (fileEnabledTypes) {
                    gallery.enabledGalleryTypes = intersection(gallery.allGalleryTypes, fileEnabledTypes);
                }

                if (galleryTypesNeedToFetchOnOpen(gallery).length) {
                    draft.error = undefined;
                    gallery.isBusy = true;
                }

                return;
            }

            case getType(selectTab): {
                const currentGallery = selectCurrentGallery(draft);

                currentGallery.currentPaneType = action.currentPaneType;
                currentGallery.preview = undefined;

                return;
            }

            case getType(hoverItem): {
                const currentGallery = selectCurrentGallery(draft);

                currentGallery.hoveredItemIndex = action.itemIndex;

                return;
            }

            case getType(previewVideoItem): {
                const currentGallery = selectCurrentGallery(draft);
                const params = action.params;

                if (params) {
                    const { itemIndex, hasPrev, hasNext } = params;

                    // сбрасываем данные по предыдущему видeо, ждем пока подгрузится инфа но повому
                    currentGallery.preview = {
                        paneType: action.paneType,
                        itemIndex,
                        hasPrev,
                        hasNext
                    };
                } else {
                    currentGallery.preview = undefined;
                }

                return;
            }

            case getType(previewError): {
                const { error } = action;
                const currentGallery = selectCurrentGallery(draft);

                if (!currentGallery.preview) {
                    return;
                }

                currentGallery.preview.error = error;

                return;
            }

            case getType(previewImageItem): {
                const currentGallery = selectCurrentGallery(draft);
                const params = action.params;

                if (!isUndefined(params)) {
                    currentGallery.preview = {
                        paneType: action.paneType,
                        ...params
                    };
                } else {
                    currentGallery.preview = undefined;
                }

                currentGallery.hoveredItemIndex = undefined;

                return;
            }

            case getType(previewAudioItem): {
                const currentGallery = selectCurrentGallery(draft);
                const params = action.params;

                if (!isUndefined(params)) {
                    const { itemIndex, itemSource } = params;

                    currentGallery.preview = {
                        paneType: action.paneType,
                        itemIndex,
                        itemSource
                    };
                } else {
                    currentGallery.preview = undefined;
                }

                return;
            }

            case getType(videoPreviewDataReceived): {
                const currentGallery = selectCurrentGallery(draft);

                if (currentGallery.preview) {
                    currentGallery.preview.itemSource = action.modalPopupItemSource;
                }

                return;
            }

            case getType(changeSelectedGalleryCategories): {
                draft.error = undefined;

                const stockPane = selectStockGalleryPane(draft);

                stockPane.isAllItemsLoading = true;
                stockPane.filters.selectedGalleryCategories = action.selectedGalleryCategories;
                return;
            }

            case getType(changeRecentNameFilter): {
                draft.error = undefined;

                const recentPane = selectRecentGalleryPane(draft);

                recentPane.filters.recentNameFilter = action.recentNameFilter;
                recentPane.isAllItemsLoading = true;

                return;
            }

            case getType(changeSemanticTextFilter): {
                draft.error = undefined;

                const stockPane = selectStockGalleryPane(draft);

                stockPane.filters.semanticText = action.semanticText;
                stockPane.isAllItemsLoading = true;

                return;
            }

            case getType(changeRecentOrder): {
                draft.error = undefined;

                const recentPane = selectRecentGalleryPane(draft);

                recentPane.filters.recentOrder = action.recentOrder;
                if (recentPane.data.collection.length > 1) {
                    recentPane.isAllItemsLoading = true;
                }

                return;
            }

            case getType(loadMoreItems): {
                draft.error = undefined;

                const currentPane = selectGalleryPane(draft);

                currentPane.isMoreItemsLoading = true;
                currentPane.filters.limit = action.limit;

                return;
            }

            case getType(removeItem): {
                draft.error = undefined;

                return;
            }

            case getType(applyItem): {
                draft.error = undefined;

                const currentGallery = selectCurrentGallery(draft);
                const currentPane = selectGalleryPane(draft);

                currentPane.isAllItemsLoading = false;
                currentPane.isMoreItemsLoading = false;
                currentGallery.preview = undefined;
                currentGallery.hoveredItemIndex = undefined;
                currentGallery.isBusy = true;

                return;
            }

            case getType(editItemName): {
                draft.error = undefined;

                const { name, itemCollectionIndex } = action;
                const paneData = selectGalleryPane(draft).data;

                paneData.collection[itemCollectionIndex].name = name;

                return;
            }

            case getType(fetchItemsSuccess): {
                const { paneType, resourceType, data } = action;
                const gallery = selectCurrentGallery(draft, resourceType);
                const pane = gallery.panes[paneType];

                pane.data = data;
                pane.isAllItemsLoading = false;
                pane.isFetched = true;

                if (!galleryTypesNeedToFetchOnOpen(gallery).length) {
                    gallery.isBusy = false;
                }

                return;
            }

            case getType(loadMoreItemsSuccess): {
                const { paneType, resourceType, data } = action;
                const gallery = selectCurrentGallery(draft, resourceType);
                const pane = gallery.panes[paneType];

                const { offset, limit } = pane.filters;
                pane.filters.offset = offset + limit;

                pane.data.collection = pane.data.collection.concat(data.collection);
                pane.data.total = data.total;
                pane.isMoreItemsLoading = false;

                return;
            }

            case getType(fetchInitialData): {
                draft.error = undefined;

                return;
            }

            case getType(removeGalleryItemSuccess): {
                const gallery = selectCurrentGallery(draft, action.resourceType);
                // только в ранее использованных можно удалить элемент
                const { filters, data } = gallery.panes[GalleryPaneTypes.recent];

                data.collection = data.collection.filter((elem, index) => index !== action.itemIndex);
                data.total -= 1;

                // если удалили последний элемент, то переключаемся на вкладку стандартные
                if (
                    data.total === 0 &&
                    !filters.recentNameFilter &&
                    gallery.enabledGalleryTypes.includes(GalleryPaneTypes.stock)
                ) {
                    gallery.currentPaneType = GalleryPaneTypes.stock;
                    gallery.preview = undefined;
                }

                return;
            }

            case getType(apiActionError): {
                draft.resources.image.isBusy = false;
                draft.resources.video.isBusy = false;
                draft.resources.audio.isBusy = false;

                draft.error = getValidationErrorData(action.message);

                return;
            }

            case getType(changeEnabledGalleryTypes): {
                const gallery = draft.resources[action.resource];

                gallery.enabledGalleryTypes = action.enabledGalleryTypes;
                gallery.allGalleryTypes = action.enabledGalleryTypes;
                gallery.preview = undefined;

                return;
            }

            default:
                return;
        }
    });
}
